import styled from "@emotion/styled";
import { useForm } from "react-hook-form";
import { H1, Label, LoadingSpinner, Select } from "atoms";
import { Head } from "features/misc";
import type {
  Artist,
  Project,
  ReviewableAffiliation,
} from "features/reviewable";
import { useUpdateArtist, useUpdateProject } from "features/reviewable";
import {
  searchKeys,
  AffiliatedReviewableInput,
  useReviewableList,
} from "features/search";
import type { SearchList } from "features/search";
import { useQueryClient } from "react-query";

type ReviewableAndImage = (Project | Artist) & { image: Project["heroImage"] };

type EditListForm = { current: SearchList; selections: ReviewableAndImage[] };

// Constants
const title = "Edit Featured Projects";
const searchLists: SearchList[] = [
  "project-of-the-month",
  "artist-of-the-month",
  "minting-now",
  "minting-soon",
  "home-hero-gallery",
];

/**
 * Format list value into displayed label
 */
const formatList = (list: string) =>
  list
    .split("-")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

function AdminFeaturedProjects() {
  // We need a form context to use the AffiliatedReviewableInput component, so store the currently
  // selected list name as well as the returned search results as field values
  const { control, register, setValue, watch } = useForm<EditListForm>({
    defaultValues: { selections: [] },
  });

  // Subscribe to changes in the Select component to trigger updates in search query
  const currentList = watch("current");

  // Use selected list name from above to query the API for array of reviewables
  const { data, ...query } = useReviewableList(currentList, {
    onSuccess: (resp) => {
      // Add returned result to selection field within AffiliatedReviewableInput
      setValue(
        "selections",
        resp.data.map((entity) => ({
          ...entity,
          // We need to convert reviewable to shape expected by ReviewableAffiliation type
          image: entity.heroImage,
        }))
      );
    },
  });

  // Prepare mutation functions for any changes to reviewables within lists
  const projectMutation = useUpdateProject();
  const artistMutation = useUpdateArtist();

  // Create a success handler to refetch lists after reviewable
  const queryClient = useQueryClient();
  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries(searchKeys.list(currentList));
    },
  };

  // Given an entity with updated projectList field, post to API
  const updateEntity = (entity: Project | Artist) => {
    if (entity.type === "PROJECT") {
      projectMutation.mutate(entity, mutationOptions);
    } else {
      artistMutation.mutate(entity, mutationOptions);
    }
  };

  // Create handler to add entity to current list
  const onAdd = (entity: Project | Artist) =>
    updateEntity({
      ...entity,
      projectLists: [...entity.projectLists, currentList],
    });

  // Create handler to remove entity from current list
  const onRemove = (selection: ReviewableAffiliation) => {
    // Get full entity data from useReviewableList result using removed selection
    const entity = data?.data.find((d) => d.id === selection.id);
    if (entity) {
      updateEntity({
        ...entity,
        projectLists: entity.projectLists.filter((l) => l !== currentList),
      });
    }
  };

  const isLoading =
    query.isLoading || projectMutation.isLoading || artistMutation.isLoading;

  return (
    <Main>
      <Head title={title} />
      <H1>{title}</H1>

      <Label>
        Select a list to edit
        <Select {...register("current")}>
          {searchLists.map((list) => (
            <option key={list} value={list}>
              {formatList(list)}
            </option>
          ))}
        </Select>
      </Label>

      {isLoading && <LoadingSpinner />}

      <AffiliatedReviewableInput<EditListForm, "selections">
        name="selections"
        control={control}
        typeLabel="Projects or Artists"
        onAdd={onAdd}
        onRemove={onRemove}
      />
    </Main>
  );
}

const Main = styled.main({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

export { AdminFeaturedProjects };
